import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { translate } from "../translations";

function Products() {
  const navigate = useNavigate();
  const [iconOpacity, setIconOpacity] = useState(1);
  const sectionRef = useRef(null);
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate when section is centered in viewport
        const sectionCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;

        // Calculate distance from viewport center in pixels
        const distanceFromCenter = Math.abs(sectionCenter - viewportCenter);

        // Define the "center zone" (±100px)
        const centerZone = 100;

        let opacity = 1;

        // If within center zone, opacity = 1
        if (distanceFromCenter <= centerZone) {
          opacity = 1;
        } else {
          // Outside center zone, fade out progressively
          const distanceOutsideZone = distanceFromCenter - centerZone;
          // Max distance to consider (from center zone edge to full fade)
          const maxDistance = windowHeight;
          // Normalize the distance (0 to 1)
          const normalizedDistance = Math.min(
            1,
            distanceOutsideZone / maxDistance
          );

          // Fade out: opacity goes from 1 to 0
          opacity = 1 - normalizedDistance * 0.8; // Keep at least 0.2 opacity
        }

        setIconOpacity(opacity);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial calculation

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section id="products" className="products-section" ref={sectionRef}>
      <div className="container">
        <div className="section-header">
          <span className="section-badge">
            {translate("productsBadge", language)}
          </span>
          <h2 className="section-title">
            {translate("productsTitle", language)}
          </h2>
          {/* <h3 className="section-subtitle">
            {translate("productsSubtitle", language)}
          </h3> */}
          <p className="section-description">
            {translate("productsDescription", language)}
          </p>
        </div>
        <div className="products-grid">
          {/* Sparkle */}
          <div
            className="product-card"
            onClick={() => navigate(`/${language}/sparkle`)}
          >
            <div
              className="product-icon sparkle-icon"
              style={{
                background: "#06B6D420",
                opacity: iconOpacity,
              }}
            >
              <img
                src="/assets/sparkle-logo.svg"
                alt="Sparkle"
                className="sparkle-logo-img"
                style={{
                  width: "3rem",
                  height: "3rem",
                  objectFit: "contain",
                }}
              />
              <div className="sparkle-stars">
                <span className="sparkle-star star-1">✦</span>
                <span className="sparkle-star star-2">✦</span>
                <span className="sparkle-star star-3">✦</span>
                <span className="sparkle-star star-4">✦</span>
                <span className="sparkle-star star-5">✦</span>
                <span className="sparkle-star star-6">✦</span>
              </div>
            </div>
            <h3 className="product-title sparkle-title">
              <span>
                <span className="sparkle-title-text">Sparkle</span>
                <span className="sparkle-title-copyright">©</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagSaaS", language)}</span>
              <span className="product-tag">{translate("tagProductivity", language)}</span>
            </div>
            <p className="product-description">
              {translate("sparkleDesc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* PassCrypt */}
          <div
            className="product-card passcrypt-card"
            onClick={() => navigate(`/${language}/passcrypt`)}
          >
            <div
              className="product-icon passcrypt-icon"
              style={{
                background: "#F59E0B20",
                opacity: iconOpacity,
              }}
            >
              <span className="passcrypt-emoji" style={{ fontSize: "2.5rem" }} aria-hidden>🔐</span>
            </div>
            <h3 className="product-title passcrypt-title">
              <span>
                <span className="passcrypt-title-text">PassCrypt</span>
                <span className="passcrypt-title-copyright">©</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagCybersecurity", language)}</span>
            </div>
            <p className="product-description">
              {translate("passcryptDesc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* ProBat */}
          <div
            className="product-card probat-card"
            onClick={() => navigate(`/${language}/probat`)}
          >
            <div
              className="product-icon probat-icon"
              style={{
                background: "#10B98120",
                opacity: iconOpacity,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Building icons in background */}
              <span
                className="probat-building-1"
                style={{ fontSize: "3.5rem", opacity: 0 }}
              >
                🏢
              </span>
              <span
                className="probat-building-2"
                style={{ fontSize: "3.5rem", opacity: 0 }}
              >
                🏢
              </span>
              <span
                className="probat-building-3"
                style={{ fontSize: "3.2rem", opacity: 0 }}
              >
                🏢
              </span>
              <span
                className="probat-crane-emoji"
                style={{ fontSize: "3rem", position: "relative", zIndex: 1 }}
              >
                🏗️
              </span>
            </div>
            <h3 className="product-title probat-title">
              <span>
                <span className="probat-title-text">ProBat</span>
                <span className="probat-title-copyright">©</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagMobile", language)}</span>
              <span className="product-tag">{translate("tagB2B", language)}</span>
            </div>
            <p className="product-description">
              {translate("probatDesc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Master Sudoku Challenge */}
          <div
            className="product-card msc-card"
            onClick={() => {
              window.open(
                "https://mastersudokuchallenge.com/",
                "_blank",
                "noopener,noreferrer"
              );
            }}
          >
            <div
              className="product-icon msc-icon"
              style={{
                background: "#8B5CF620",
                opacity: iconOpacity,
              }}
            >
              <img
                src="/assets/msudokuchallenge.png"
                alt="Master Sudoku Challenge"
                style={{
                  width: "3rem",
                  height: "3rem",
                  objectFit: "contain",
                }}
              />
            </div>
            <h3 className="product-title msc-title">
              <span>
                <span className="msc-title-text">Master Sudoku Challenge</span>
                <span className="msc-title-copyright">©</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagMobile", language)}</span>
              <span className="product-tag">{translate("tagGame", language)}</span>
            </div>
            <p className="product-description">
              {translate("masterSudokuDesc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="msc-store-logos">
              <a
                href="https://apps.apple.com/app/master-sudoku-challenge/id"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="store-logo-link"
              >
                <img
                  src="/assets/appstore.svg"
                  alt="Download on App Store"
                  className="store-logo"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id="
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="store-logo-link"
              >
                <img
                  src="/assets/googlestore.png"
                  alt="Get it on Google Play"
                  className="store-logo"
                />
              </a>
            </div>
          </div>

          {/* PandaGPT */}
          <div
            className="product-card pandagpt-card"
            onClick={() => navigate(`/${language}/pandagpt`)}
          >
            <div
              className="product-icon pandagpt-icon"
              style={{
                background: "#10B98120",
                opacity: iconOpacity,
              }}
            >
              <span className="pandagpt-emoji" style={{ fontSize: "2.5rem" }} aria-hidden>🐼</span>
            </div>
            <h3 className="product-title pandagpt-title">
              <span>
                <span className="pandagpt-title-text">PandaGPT</span>
                <span className="pandagpt-title-copyright">©</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagAI", language)}</span>
              <span className="product-tag">{translate("tagRAG", language)}</span>
            </div>
            <p className="product-description">
              {translate("pandagptDesc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* VPS RAM-oriented Hosting */}
          <div className="product-card">
            <div
              className="product-icon"
              style={{
                background: "#3B82F620",
                opacity: iconOpacity,
              }}
            >
              <span style={{ fontSize: "2.5rem" }} aria-hidden>
                🖥️
              </span>
            </div>
            <h3 className="product-title hp-memory-title">
              <span className="hp-memory-title-text">MEMORY MACHINES</span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagHosting", language)}</span>
              <span className="product-tag">{translate("tagB2B", language)}</span>
            </div>
            <p className="product-description">
              {translate("vpsRamDesc", language)}
            </p>
            <div
              className="product-link"
              style={{ opacity: 0.5, pointerEvents: "none" }}
            >
              <span>{translate("learnMore", language)}</span>
            </div>
          </div>

          {/* NF525 Certification */}
          <div
            className="product-card"
            onClick={() => navigate(`/${language}/nf525`)}
          >
            <div
              className="product-icon"
              style={{
                background: "#14B8A620",
                opacity: iconOpacity,
              }}
            >
              <span style={{ fontSize: "2.5rem" }} aria-hidden>
                ✅
              </span>
            </div>
            <h3 className="product-title nf525-ready-title">
              <span className="nf525-ready-title-text">NF525 Ready</span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">
                {translate("tagCompliance", language)}
              </span>
              <span className="product-tag">
                {translate("tagCybersecurity", language)}
              </span>
            </div>
            <p className="product-description">
              {translate("nf525Desc", language)}
            </p>
            <div className="product-link">
              <span>{translate("learnMore", language)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* AI Product - In Progress */}
          <div
            className="product-card ai-product-card"
            onMouseEnter={(e) => {
              const svg = e.currentTarget.querySelector(".ai-logo-svg");
              if (svg) {
                const animations = svg.querySelectorAll("animateTransform");
                animations.forEach((anim) => {
                  anim.beginElement();
                });
              }
            }}
            onMouseLeave={(e) => {
              const svg = e.currentTarget.querySelector(".ai-logo-svg");
              if (svg) {
                const animations = svg.querySelectorAll("animateTransform");
                animations.forEach((anim) => {
                  anim.endElement();
                });
              }
            }}
          >
            {/* <div className="in-progress-badge">In Progress</div> */}
            <div
              className="product-icon ai-icon"
              style={{
                background: "#6366F120",
                opacity: iconOpacity,
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ai-logo-svg"
              >
                <defs>
                  <linearGradient
                    id="aiGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="rotate(0 40 40)"
                  >
                    <animateTransform
                      attributeName="gradientTransform"
                      type="rotate"
                      values="0 40 40;360 40 40"
                      dur="3s"
                      repeatCount="indefinite"
                    />
                    <stop
                      offset="0%"
                      style={{ stopColor: "#6366F1", stopOpacity: 1 }}
                    />
                    <stop
                      offset="50%"
                      style={{ stopColor: "#8B5CF6", stopOpacity: 1 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: "#A855F7", stopOpacity: 1 }}
                    />
                  </linearGradient>
                </defs>

                {/* Fixed circle with rotating gradient */}
                <circle
                  cx="40"
                  cy="40"
                  r="30"
                  fill="none"
                  stroke="url(#aiGradient)"
                  strokeWidth="3"
                />

                {/* Logo in center with slow rotation and gentle scale - animated on hover */}
                <g transform="translate(40, 40)" className="ai-logo-container">
                  <g>
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      values="0;360"
                      dur="20s"
                      repeatCount="indefinite"
                      begin="indefinite"
                    />
                    <image
                      href="/assets/ygalaad_icon_transparent.svg"
                      x="-18"
                      y="-18"
                      width="36"
                      height="36"
                      className="ai-logo-center"
                    >
                      <animateTransform
                        attributeName="transform"
                        type="scale"
                        values="1;1.05;1"
                        dur="3s"
                        repeatCount="indefinite"
                        transform-origin="18 18"
                        begin="indefinite"
                      />
                    </image>
                  </g>
                </g>
              </svg>
            </div>
            <h3 className="product-title ai-title">
              <span>
                <span className="ai-title-text">AI Powered</span>
                <span className="ai-badge-beta">Coming Soon</span>
              </span>
            </h3>
            <div className="product-tags">
              <span className="product-tag">{translate("tagAI", language)}</span>
            </div>
            <p className="product-description">
              {translate("aiProductDesc", language)}
            </p>
            <div
              className="product-link"
              style={{ opacity: 0.5, pointerEvents: "none" }}
            >
              {/* <span>{translate("comingSoon", language)}</span> */}
              <span>{translate("learnMore", language)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Products;
